import React, { useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AdminLayout } from '../../dashboard/components/AdminLayout';
import {
  ProgressSidebar,
  OnboardingStep,
  UploadStep,
  ProcessingStep,
  FormStep,
  FinalizationStep,
  CompletionStep,
} from '../../../enrichment/components';
import { makeAdminCompanySource } from '../../../enrichment/companySources';
import { useEnrichmentFlow } from '../../../enrichment/hooks';
import { COLORS } from '../../../../core/constants/colors';
import { useSectors } from '../../companies/hooks/useSectors';

const ADMIN_FEATURES = {
  markEnrichmentCompleteOnFinalize: false,
  writeLocalEnrichmentStatusOnSkip: false,
  completeAndExitOnSkip: false,
  enableOverviewStep: false,
};

export const AdminEnrichmentPage: React.FC = () => {
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  const { sectors, createSector } = useSectors();

  const numericCompanyId = companyId ? parseInt(companyId) : null;
  const companySource = useMemo(
    () => makeAdminCompanySource(numericCompanyId),
    [numericCompanyId],
  );

  const navigateToCompany = () => {
    navigate(`/admin/companies/${companyId}`, { replace: true });
  };

  const flow = useEnrichmentFlow({
    mode: 'admin',
    companySource,
    features: ADMIN_FEATURES,
    onAfterFinalize: navigateToCompany,
    onAfterSkipToCompletion: navigateToCompany,
  });

  if (flow.companyLoading) {
    return (
      <AdminLayout pageTitle="Enrichment" pageDescription="Run normal enrichment for company">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div
              className="w-12 h-12 rounded-full border-4 border-t-transparent mx-auto animate-spin"
              style={{ borderColor: COLORS.admin.primary }}
            />
            <p className="mt-4 text-sm" style={{ color: COLORS.admin.primary }}>
              Loading company data...
            </p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout
      pageTitle="Company Enrichment"
      pageDescription={`Run company enrichment for ${flow.company?.name || 'company'}`}
    >
      <div className="min-h-screen flex" style={{ backgroundColor: COLORS.extended.cultured }}>
        <div className="flex-shrink-0 w-72 pl-10 pr-8 py-12">
          <ProgressSidebar steps={flow.progressSteps} />
        </div>

        <div className="flex-1 bg-white h-screen overflow-y-auto">
          <div className="max-w-4xl mx-auto px-12 py-12">
            {flow.flowStep === 'onboarding' && (
              <OnboardingStep
                companyForm={flow.companyForm}
                onInputChange={flow.handleCompanyInputChange}
                onSectorChange={flow.handleSectorChange}
                onSave={flow.handleSaveCompany}
                saving={flow.savingCompany}
                error={flow.companyError}
                sectors={sectors}
                onCreateSector={createSector}
              />
            )}

            {flow.flowStep === 'upload' && (
              <UploadStep
                company={flow.company}
                existingDocuments={flow.existingDocuments}
                selectedExistingDocumentIds={flow.selectedExistingDocumentIds}
                loadingDocuments={flow.loadingDocuments}
                onToggleExistingDocument={flow.handleToggleExistingDocument}
                onUploadComplete={flow.handleInitialUploadComplete}
                onStartEnrichment={flow.handleStartEnrichment}
                onBack={() => flow.setFlowStep('onboarding')}
                readyToStart={flow.readyToStart}
                startingSession={flow.startingSession}
                sessionError={flow.sessionError}
                existingSessionConflict={flow.existingSessionConflict}
                onViewExistingSession={flow.handleViewExistingSession}
                onSkipExistingSession={flow.handleSkipExistingSession}
                onCancelExistingSession={flow.handleCancelExistingSession}
                onCloseConflictModal={flow.closeConflictModal}
              />
            )}

            {flow.flowStep === 'processing' && (
              <ProcessingStep
                enrichmentCompleted={flow.enrichmentCompleted}
                processingAfterSubmit={flow.processingAfterSubmit}
                processingMessage={flow.processingMessage}
                session={flow.session}
                sessionError={flow.sessionError}
                checkingStatus={flow.checkingStatus}
                loadingForm={flow.loadingForm}
                documentCount={
                  flow.selectedExistingDocumentIds.length + flow.initialDocumentIds.length
                }
                onBack={() => flow.setFlowStep('upload')}
                onViewSession={flow.handleViewSessionWhileWaiting}
                onSkipEnrichment={flow.handleSkipEnrichment}
                onCancelSession={flow.handleCancelFromProcessing}
                onLoadForm={flow.handleLoadForm}
                onSkipToDashboard={flow.handleSkipToDashboardFromProcessing}
              />
            )}

            {flow.flowStep === 'form' && (
              <FormStep
                session={flow.session}
                formData={flow.formData}
                fieldResponses={flow.fieldResponses}
                additionalDocumentIds={flow.additionalDocumentIds}
                company={flow.company}
                submittingResponses={flow.submittingResponses}
                sessionError={flow.sessionError}
                responseMessage={flow.responseMessage}
                onBack={() => flow.setFlowStep('processing')}
                onFieldChange={flow.handleFieldChange}
                onAdditionalUploadComplete={flow.handleAdditionalUploadComplete}
                onSubmitResponses={flow.handleSubmitResponses}
                onSkipEnrichment={flow.handleSkipEnrichment}
              />
            )}

            {flow.flowStep === 'finalization' && (
              <FinalizationStep
                finalizeMessage={flow.finalizeMessage}
                finalizing={flow.finalizing}
                onBack={() => flow.setFlowStep('form')}
                onFinalize={flow.handleFinalize}
              />
            )}

            {flow.flowStep === 'completed' && <CompletionStep />}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};
